"use client"

import { useTheme } from "next-themes"
import { Toaster as Sonner } from "sonner"
import { useEffect, useState } from "react"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ 
  position = "bottom-right", // Set default position to bottom-right
  ...props 
}: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const [isMounted, setIsMounted] = useState(false)
  const [isDesktop, setIsDesktop] = useState(true)

  // Only mount the toaster on the client side
  useEffect(() => {
    setIsMounted(true)
    
    // Check if desktop and set state
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768) // 768px is typical md breakpoint
    }
    
    // Set initial value
    handleResize()
    
    // Add event listener
    window.addEventListener('resize', handleResize)
    
    // Cleanup
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!isMounted) {
    return null
  }

  // Force bottom-right position on desktop regardless of what props are passed
  const finalPosition = isDesktop ? "bottom-right" : position

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      position={finalPosition}
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg group-[.toaster]:pointer-events-auto",
          title: "text-foreground font-semibold",
          description: "text-sm text-color-foreground/80",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          closeButton:
            "group-[.toast]:opacity-70 group-[.toast]:text-foreground group-[.toast]:hover:opacity-100",
          // Add specific styling for toast types using the newer data attribute selectors
          error: 
            "group-data-[type=error]:border-red-500 group-data-[type=error]:bg-red-50 dark:group-data-[type=error]:bg-red-950/50 group-data-[type=error]:text-red-800 dark:group-data-[type=error]:text-red-300 [&>[data-description]]:text-red-800 dark:[&>[data-description]]:text-red-300",
          success: 
            "group-data-[type=success]:border-green-500 group-data-[type=success]:bg-green-50 dark:group-data-[type=success]:bg-green-950/50 group-data-[type=success]:text-green-800 dark:group-data-[type=success]:text-green-300 [&>[data-description]]:text-green-800 dark:[&>[data-description]]:text-green-300",
          warning: 
            "group-data-[type=warning]:border-yellow-500 group-data-[type=warning]:bg-yellow-50 dark:group-data-[type=warning]:bg-yellow-950/50 group-data-[type=warning]:text-yellow-800 dark:group-data-[type=warning]:text-yellow-300 [&>[data-description]]:text-yellow-800 dark:[&>[data-description]]:text-yellow-300",
          info: 
            "group-data-[type=info]:border-blue-500 group-data-[type=info]:bg-blue-50 dark:group-data-[type=info]:bg-blue-950/50 group-data-[type=info]:text-blue-800 dark:group-data-[type=info]:text-blue-300 [&>[data-description]]:text-blue-800 dark:[&>[data-description]]:text-blue-300",
          // Add icon styling based on toast type
          icon: 
            "group-data-[type=error]:text-red-600 group-data-[type=success]:text-green-600 group-data-[type=warning]:text-yellow-600 group-data-[type=info]:text-blue-600 dark:group-data-[type=error]:text-red-400 dark:group-data-[type=success]:text-green-400 dark:group-data-[type=warning]:text-yellow-400 dark:group-data-[type=info]:text-blue-400",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
